import type { ShortcutMap } from '@vueron/kiska';

export const SHORTCUT_NAMESPACE = {
  //   editor: 'editor',
  viewer: 'viewer',
  default: 'default',
  simulation: 'simulation',
  zoneslot: 'zoneslot',
  zone: 'zone',
} as const;

const defaultShortcutDefinition = {
  default: [],
  simulation: [
    { category: 'edit', command: 'cancel', key: ['esc'] },
    { category: 'edit', command: 'copy', key: ['ctrl+c', 'cmd+c'] },
    { category: 'tool', command: 'mouseModePivot', key: ['t'] },
    { category: 'tool', command: 'cycleSimulationView', key: ['v'] },
    { category: 'edit', command: 'paste', key: ['ctrl+v', 'cmd+v'] },
    {
      category: 'edit',
      command: 'removeSelected',
      key: ['backspace', 'delete'],
    },
    { category: 'edit', command: 'redo', key: ['ctrl+shift+z', 'cmd+shift+z'] },
    {
      category: 'file',
      command: 'save',
      key: ['ctrl+s', 'cmd+s'],
    },
    {
      category: 'file',
      command: 'saveSceneToFile',
      key: ['ctrl+shift+s', 'cmd+shift+s'],
    },
    { category: 'edit', command: 'undo', key: ['ctrl+z', 'cmd+z'] },
  ],
  // 존 편집기 전용 키맵. EditingZone 이 마운트될 때 switchNamespace('zone').
  zone: [
    { category: 'edit', command: 'cancel', key: ['esc'] },
    { category: 'edit', command: 'undo', key: ['ctrl+z', 'cmd+z'] },
    { category: 'edit', command: 'redo', key: ['ctrl+shift+z', 'cmd+shift+z'] },
    { category: 'edit', command: 'redoAlt', key: ['ctrl+y', 'cmd+y'] },
    { category: 'edit', command: 'duplicate', key: ['ctrl+d', 'cmd+d'] },
    { category: 'edit', command: 'selectAll', key: ['ctrl+a', 'cmd+a'] },
    {
      category: 'edit',
      command: 'removeSelected',
      key: ['backspace', 'delete'],
    },
    { category: 'edit', command: 'focusNextVertex', key: ['tab'] },
    { category: 'edit', command: 'focusPrevVertex', key: ['shift+tab'] },
    { category: 'file', command: 'save', key: ['ctrl+s', 'cmd+s'] },
    { category: 'edit', command: 'submit', key: ['enter'] },
    { category: 'tool', command: 'toolSelect', key: ['s'] },
    { category: 'tool', command: 'toolCreate', key: ['c'] },
    { category: 'tool', command: 'toolMove', key: ['m'] },
    { category: 'tool', command: 'toggleOrtho', key: ['o'] },
    { category: 'tool', command: 'topDownView', key: ['2'] },
    { category: 'tool', command: 'toggleLabels', key: ['l'] },
    { category: 'tool', command: 'toggleHUD', key: ['h'] },
    // 방향키 nudge (선택된 vertex 만; 존 전체는 M 툴로).
    { category: 'edit', command: 'moveUp', key: ['up'] },
    { category: 'edit', command: 'moveDown', key: ['down'] },
    { category: 'edit', command: 'moveLeft', key: ['left'] },
    { category: 'edit', command: 'moveRight', key: ['right'] },
    { category: 'edit', command: 'moveUpLarge', key: ['shift+up'] },
    { category: 'edit', command: 'moveDownLarge', key: ['shift+down'] },
    { category: 'edit', command: 'moveLeftLarge', key: ['shift+left'] },
    { category: 'edit', command: 'moveRightLarge', key: ['shift+right'] },
  ],
  // 파크슬롯 편집기 전용 키맵. ZoneSlotEditor 가 마운트될 때 switchNamespace('zoneslot').
  zoneslot: [
    { category: 'edit', command: 'cancel', key: ['esc'] },
    { category: 'edit', command: 'copy', key: ['ctrl+c', 'cmd+c'] },
    { category: 'edit', command: 'paste', key: ['ctrl+v', 'cmd+v'] },
    { category: 'edit', command: 'cut', key: ['ctrl+x', 'cmd+x'] },
    { category: 'edit', command: 'duplicate', key: ['ctrl+d', 'cmd+d'] },
    { category: 'edit', command: 'selectAll', key: ['ctrl+a', 'cmd+a'] },
    {
      category: 'edit',
      command: 'removeSelected',
      key: ['backspace', 'delete'],
    },
    { category: 'edit', command: 'undo', key: ['ctrl+z', 'cmd+z'] },
    { category: 'edit', command: 'redo', key: ['ctrl+shift+z', 'cmd+shift+z'] },
    { category: 'edit', command: 'redoAlt', key: ['ctrl+y', 'cmd+y'] },
    { category: 'file', command: 'save', key: ['ctrl+s', 'cmd+s'] },
    // 방향키 이동 — nudge. shift 조합은 large step.
    { category: 'edit', command: 'moveUp', key: ['up'] },
    { category: 'edit', command: 'moveDown', key: ['down'] },
    { category: 'edit', command: 'moveLeft', key: ['left'] },
    { category: 'edit', command: 'moveRight', key: ['right'] },
    { category: 'edit', command: 'moveUpLarge', key: ['shift+up'] },
    { category: 'edit', command: 'moveDownLarge', key: ['shift+down'] },
    { category: 'edit', command: 'moveLeftLarge', key: ['shift+left'] },
    { category: 'edit', command: 'moveRightLarge', key: ['shift+right'] },
  ],
  viewer: [
    {
      category: 'operation',
      command: 'save',
      key: ['ctrl+s', 'cmd+s'],
    },
    {
      category: 'operation',
      command: 'reset',
      key: ['ctrl+r', 'cmd+r'],
    },
    {
      category: 'operation',
      command: 'toggleCalibrationPanel',
      key: ['c'],
    },
    {
      category: 'operation',
      command: 'toggleZonePanel',
      key: ['z'],
    },
    {
      category: 'operation',
      command: 'toggleVObjectTooltip',
      key: ['i'],
    },
    {
      category: 'operation',
      command: 'toggleVObject',
      key: ['b'],
    },
    {
      category: 'operation',
      command: 'toggleGrid',
      key: ['g'],
    },
    {
      category: 'operation',
      command: 'toggleTrajectory',
      key: ['t'],
    },
    {
      category: 'operation',
      command: 'toggleDebugObject',
      key: ['d'],
    },
    {
      category: 'operation',
      command: 'togglePCD',
      key: ['p'],
    },
    {
      // 클러스터 검사 (선택 모드) 토글. paused frame 에서만 의미 — VPF-00 cluster
      // inspection. 'i'/'s' 가 이미 다른 토글에 잡혀 있어 충돌 회피로 shift+s.
      category: 'operation',
      command: 'toggleFrameInspection',
      key: ['shift+s'],
    },
    {
      // ESC: 활성 모달 취소. 현재는 선택 모드만 처리 (확장 가능).
      category: 'edit',
      command: 'cancel',
      key: ['esc'],
    },
  ],
} as const;

// =========================================================
// 프로그램적으로 자동완성됨

type ShortcutCommandKey =
  (typeof defaultShortcutDefinition)[keyof typeof defaultShortcutDefinition][number]['command'];

export const SHORTCUT_COMMAND = Object.fromEntries(
  [
    ...new Set(
      (
        Object.keys(
          defaultShortcutDefinition,
        ) as (keyof typeof defaultShortcutDefinition)[]
      ).flatMap(namespace =>
        defaultShortcutDefinition[namespace].map(item => item.command),
      ),
    ),
  ].map(command => [command, command]),
) as { [K in ShortcutCommandKey]: K };
// export const SHORTCUT_COMMAND = {
//   copy: 'copy',
//   paste: 'paste',
//   undo: 'undo',
//   redo: 'redo',
//   save: 'save',
// } as const;

export const defaultShortcut =
  defaultShortcutDefinition as unknown as ShortcutMap<
    typeof SHORTCUT_NAMESPACE,
    typeof SHORTCUT_COMMAND
  >;
